"use client";

import { useRef, useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ForceRow,
  NetForceInputs,
  NetForceResults,
  type NetState,
} from "@/components/tools/physics-mechanics/force-net-panel";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  FRICTION,
  FRICTION_LEVEL,
  type FrictionKey,
  type LawKey,
  type LevelFrictionKey,
  SECOND_LAW,
  WEIGHT,
  type WeightKey,
} from "@/lib/tool-kit/physics-mechanics/force";
import { SIG_FIG_CHOICES } from "@/lib/tool-kit/physics-mechanics/format";
import { GRAVITY_PRESETS } from "@/lib/tool-kit/physics-mechanics/projectile";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";

/** English strings. A translated content file overrides these through `ui`. */
export const FORCE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeLaw: "Force, mass or acceleration (F = ma)",
  modeWeight: "Weight (W = mg)",
  modeFriction: "Friction (f = μN)",
  modeNet: "Net force of several forces",
  lawHint:
    "Newton's second law: the net force on an object equals its mass times its acceleration. Enter the net force, not just one of the forces acting.",
  weightHint:
    "Weight is the gravitational force on a mass. Mass stays the same everywhere; weight depends on the local g.",
  frictionHint:
    "Friction opposes sliding. Use the static coefficient μs for the largest friction before motion starts, and the kinetic coefficient μk once the surfaces slide.",
  frictionSource: "The normal force is",
  frictionGiven: "a value I know (N)",
  frictionLevel: "from a mass on level ground (N = mg)",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  gravity: "Gravity",
  gravityEarth: "Earth (9.80665 m/s², standard gravity)",
  gravityMoon: "Moon (1.62 m/s²)",
  gravityMars: "Mars (3.73 m/s², mean)",
  gravityCustom: "Another value",
  results: "Results",
  force: "Net force F",
  mass: "Mass m",
  acceleration: "Acceleration a",
  weight: "Weight W",
  gravityField: "Gravitational acceleration g",
  friction: "Friction force f",
  mu: "Coefficient of friction μ",
  normal: "Normal force N",
  muHint: "A pure number, usually between 0 and about 1.5.",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  errors: {
    "mass-not-positive": "Mass must be greater than zero.",
    "zero-acceleration":
      "With zero acceleration there is no net force, so the mass cannot be found from F = ma. Enter an acceleration that is not zero.",
    "mass-sign":
      "Mass cannot be negative. The force and acceleration must point the same way (the same sign) for F = ma to give a mass.",
    "gravity-not-positive": "g must be greater than zero.",
    "weight-not-positive": "Weight must be greater than zero.",
    "mu-negative": "The coefficient of friction cannot be negative.",
    "normal-negative": "The normal force cannot be negative.",
    "friction-negative": "The friction force cannot be negative.",
    "normal-zero":
      "With no normal force there is nothing for friction to act on, so μ cannot be found.",
    "mu-zero":
      "With μ = 0 there is no friction, so this quantity cannot be found from f = μN.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  netHint:
    "Give each force's size and its direction as an angle counter-clockwise from the positive x axis: 0° points right, 90° up, 180° left, 270° down. All the forces act on one object.",
  forceLabel: "Force {n}",
  forceMagnitude: "Size of force {n}",
  forceAngle: "Direction of force {n}",
  addForce: "Add a force",
  removeForce: "Remove force {n}",
  massOptional: "Mass of the object (optional)",
  massHint: "Add a mass to get the acceleration produced by the net force.",
  netForce: "Net force",
  netDirection: "Direction of the net force",
  sumX: "Sum of the x components, ΣFx",
  sumY: "Sum of the y components, ΣFy",
  balanced:
    "The forces cancel: the net force is zero, so the object is in equilibrium — it stays at rest or keeps moving at constant velocity.",
  stepComponent:
    "Force {n}: Fx = {f} × cos({angle}°) = {fx}; Fy = {f} × sin({angle}°) = {fy}.",
  stepSum: "Add the components: ΣFx = {fx}, ΣFy = {fy}.",
  stepMagnitude:
    "Net force = √(ΣFx² + ΣFy²) = √(({fx})² + ({fy})²) = {magnitude}.",
  stepDirection:
    "Direction = atan2(ΣFy, ΣFx) = atan2({fy}, {fx}) = {angle}°, measured counter-clockwise from +x.",
  stepAcceleration:
    "Acceleration = net force ÷ mass = {force} N ÷ {mass} kg = {result} m/s².",
  tableHeading: "Components of each force",
  columnForce: "Force",
  columnSize: "Size",
  columnAngle: "Angle",
  columnFx: "Fx",
  columnFy: "Fy",
  diagramHeading: "Free-body diagram",
  diagramTitle:
    "Free-body diagram: each force drawn as an arrow from the object at its direction, with the net force as a dashed arrow",
  diagramNet: "Net",
  diagramNote:
    "Arrow lengths are drawn to one scale. The dashed arrow is the net force; the coloured arrows are the forces you entered.",
  netUnreadable: "Force {n} needs a size and an angle, both as plain numbers.",
  netNeedForce: "Enter at least one force.",
  netNegative:
    "Force {n} has a negative size. Give sizes as positive numbers and use the angle for direction.",
  massNotPositive: "Mass must be greater than zero.",
} as const;

type Mode = "law" | "weight" | "friction" | "net";
type FrictionSource = "given" | "level";

const EARTH = { value: "9.80665", unit: "mps2" };

const INITIAL_LAW = initialRelationState<LawKey>(SECOND_LAW, "F", {
  m: { value: "1200", unit: "kg" },
  a: { value: "2.5", unit: "mps2" },
});
const INITIAL_WEIGHT = initialRelationState<WeightKey>(WEIGHT, "W", {
  m: { value: "70", unit: "kg" },
  g: EARTH,
});
const INITIAL_FRICTION = initialRelationState<FrictionKey>(FRICTION, "f", {
  mu: { value: "0.4", unit: "1" },
  N: { value: "200", unit: "N" },
});
const INITIAL_LEVEL = initialRelationState<LevelFrictionKey>(
  FRICTION_LEVEL,
  "f",
  {
    mu: { value: "0.4", unit: "1" },
    m: { value: "10", unit: "kg" },
    g: EARTH,
  }
);
const INITIAL_NET: NetState = {
  rows: [
    { id: 1, magnitude: "10", unit: "N", angle: "0", angleUnit: "deg" },
    { id: 2, magnitude: "6", unit: "N", angle: "90", angleUnit: "deg" },
    { id: 3, magnitude: "4", unit: "N", angle: "180", angleUnit: "deg" },
  ] satisfies ForceRow[],
  mass: "2",
  massUnit: "kg",
};

function gravityPreset(entry: { value: string; unit: string }): string {
  if (entry.unit === "mps2") {
    for (const [name, g] of Object.entries(GRAVITY_PRESETS)) {
      if (Number(entry.value) === g) {
        return name;
      }
    }
  }
  return "custom";
}

export default function ForceCalculatorWidget() {
  const strings = useToolUi(FORCE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("law");
  const [law, setLaw] = useState(INITIAL_LAW);
  const [weight, setWeight] = useState(INITIAL_WEIGHT);
  const [friction, setFriction] = useState(INITIAL_FRICTION);
  const [level, setLevel] = useState(INITIAL_LEVEL);
  const [source, setSource] = useState<FrictionSource>("given");
  const [net, setNet] = useState(INITIAL_NET);
  const [sigFigs, setSigFigs] = useState("4");
  const nextId = useRef(10);
  const sig = Number(sigFigs);

  const lawLabels: Record<LawKey, string> = {
    F: strings.force,
    m: strings.mass,
    a: strings.acceleration,
  };
  const weightLabels: Record<WeightKey, string> = {
    W: strings.weight,
    m: strings.mass,
    g: strings.gravityField,
  };
  const frictionLabels: Record<FrictionKey, string> = {
    f: strings.friction,
    mu: strings.mu,
    N: strings.normal,
  };
  const levelLabels: Record<LevelFrictionKey, string> = {
    f: strings.friction,
    mu: strings.mu,
    m: strings.mass,
    g: strings.gravityField,
  };
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const lawEval = evaluateRelation(SECOND_LAW, law);
  const weightEval = evaluateRelation(WEIGHT, weight);
  const frictionEval = evaluateRelation(FRICTION, friction);
  const levelEval = evaluateRelation(FRICTION_LEVEL, level);
  const preset = gravityPreset(weight.values.g);

  const setPreset = (name: string) => {
    if (name === "custom") {
      return;
    }
    const g = GRAVITY_PRESETS[name as keyof typeof GRAVITY_PRESETS];
    setWeight({
      ...weight,
      values: { ...weight.values, g: { value: String(g), unit: "mps2" } },
    });
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="fc-mode"
        onChange={setMode}
        options={[
          { value: "law", label: strings.modeLaw },
          { value: "weight", label: strings.modeWeight },
          { value: "friction", label: strings.modeFriction },
          { value: "net", label: strings.modeNet },
        ]}
        value={mode}
      />
      {mode === "law" ? (
        <>
          <p className={HINT}>{strings.lawHint}</p>
          <RelationInputs
            {...common}
            def={SECOND_LAW}
            evaluation={lawEval}
            idPrefix="fc-law"
            labels={lawLabels}
            onState={setLaw}
            state={law}
          />
        </>
      ) : null}
      {mode === "weight" ? (
        <>
          <p className={HINT}>{strings.weightHint}</p>
          <SelectField
            id="fc-gravity"
            label={strings.gravity}
            onChange={setPreset}
            options={[
              { value: "earth", label: strings.gravityEarth },
              { value: "moon", label: strings.gravityMoon },
              { value: "mars", label: strings.gravityMars },
              { value: "custom", label: strings.gravityCustom },
            ]}
            value={preset}
          />
          <RelationInputs
            {...common}
            def={WEIGHT}
            evaluation={weightEval}
            idPrefix="fc-weight"
            labels={weightLabels}
            onState={setWeight}
            state={weight}
          />
        </>
      ) : null}
      {mode === "friction" ? (
        <>
          <p className={HINT}>{strings.frictionHint}</p>
          <PillGroup
            label={strings.frictionSource}
            name="fc-friction-source"
            onChange={setSource}
            options={[
              { value: "given", label: strings.frictionGiven },
              { value: "level", label: strings.frictionLevel },
            ]}
            value={source}
          />
          {source === "given" ? (
            <RelationInputs
              {...common}
              def={FRICTION}
              evaluation={frictionEval}
              hints={{ mu: strings.muHint }}
              idPrefix="fc-friction"
              labels={frictionLabels}
              onState={setFriction}
              state={friction}
            />
          ) : (
            <RelationInputs
              {...common}
              def={FRICTION_LEVEL}
              evaluation={levelEval}
              hints={{ mu: strings.muHint }}
              idPrefix="fc-level"
              labels={levelLabels}
              onState={setLevel}
              state={level}
            />
          )}
        </>
      ) : null}
      {mode === "net" ? (
        <>
          <p className={HINT}>{strings.netHint}</p>
          <NetForceInputs
            nextId={() => {
              nextId.current += 1;
              return nextId.current;
            }}
            onState={setNet}
            state={net}
            strings={strings}
          />
        </>
      ) : null}
      <div className="w-60">
        <SelectField
          id="fc-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "law" ? (
          <RelationResults
            {...common}
            def={SECOND_LAW}
            evaluation={lawEval}
            labels={lawLabels}
            state={law}
          />
        ) : null}
        {mode === "weight" ? (
          <RelationResults
            {...common}
            def={WEIGHT}
            evaluation={weightEval}
            labels={weightLabels}
            state={weight}
          />
        ) : null}
        {mode === "friction" && source === "given" ? (
          <RelationResults
            {...common}
            def={FRICTION}
            evaluation={frictionEval}
            labels={frictionLabels}
            state={friction}
          />
        ) : null}
        {mode === "friction" && source === "level" ? (
          <RelationResults
            {...common}
            def={FRICTION_LEVEL}
            evaluation={levelEval}
            labels={levelLabels}
            state={level}
          />
        ) : null}
        {mode === "net" ? (
          <NetForceResults fmt={fmt} sig={sig} state={net} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
